/**
 * Compact chip rendering a detected video language. Shared between:
 *   - OneVideo title-row header  (always-visible next to the channel)
 *   - TranscriptPanel collapsed header (admin only)
 *
 * Renders null when no language was detected — we don't want a
 * "language: ?" chip for unscored / legacy videos, just the absence
 * of any chip.
 */
import React from 'react';
import {Chip, Tooltip} from '@mui/material';
import {pcolors} from '../../theme/planapusTheme';

/**
 * Structural prop shape — the chip doesn't care whether the caller got
 * the language from `getVideoLanguage` (returns `LanguageFacts`) or
 * from `getAdminVideoTranscript.language` (returns `VideoLanguage`).
 * Both expose the same four fields with identical semantics; typing
 * against the fields directly avoids one call site needing to coerce
 * into a nominal type it doesn't have.
 */
export interface LanguageChipData {
  code: string;
  confidence: number;
  source: string;
  secondary?: string | null;
}

// Resolve an ISO 639 language code to its English display name using
// the browser's built-in CLDR-backed `Intl.DisplayNames`. Handles both
// ISO 639-1 (`en`, `ar`) and 639-3 (`zlm` → Malay, `cmn` → Mandarin
// Chinese, `arb` → Standard Arabic) plus BCP-47 subtags in modern
// browsers. Memoized per-code since constructing DisplayNames isn't
// free and we render the chip on every video tile.
//
// Manual fallbacks handle two edge cases:
//   1. Older browsers / Safari versions where `Intl.DisplayNames`
//      doesn't recognize every 639-3 tag — we provide common ones
//      ourselves so the chip still reads as a real name.
//   2. `DisplayNames.of()` returns the input string unchanged for
//      truly unknown codes ("xyz" → "xyz"). We detect that and
//      uppercase the code rather than showing lowercase garbage.
//
// Why not hand-roll the full map: ISO 639 has 8,000+ codes.
// `Intl.DisplayNames` ships with CLDR — we don't want to maintain a
// parallel list.
const NAME_FALLBACKS: Record<string, string> = {
  arb: 'Arabic',
  cmn: 'Mandarin Chinese',
  pes: 'Persian',
  zlm: 'Malay',
};

const nameCache = new Map<string, string>();
const displayNames = (() => {
  try {
    return new Intl.DisplayNames(['en'], {type: 'language'});
  } catch {
    return null;
  }
})();

function codeToName(code: string): string {
  const key = code.toLowerCase();
  const cached = nameCache.get(key);
  if (cached) return cached;

  let name = NAME_FALLBACKS[key];
  if (!name && displayNames) {
    try {
      const resolved = displayNames.of(key);
      // `of()` returns the input unchanged for unknown codes; treat
      // that as no-match so the uppercase-code fallback kicks in.
      if (resolved && resolved.toLowerCase() !== key) {
        name = resolved;
      }
    } catch {
      // malformed tag — fall through
    }
  }
  const final = name || code.toUpperCase();
  nameCache.set(key, final);
  return final;
}

interface Props {
  language: LanguageChipData | null | undefined;
  /**
   * Show the confidence score inline with the name. Defaults to false
   * (clean "English" chip for the title row); the transcript panel
   * passes `true` so the admin gets the confidence number too.
   */
  showConfidence?: boolean;
}

export function LanguageChip({language, showConfidence = false}: Props) {
  if (!language) return null;
  const primaryName = codeToName(language.code);
  const secondaryName = language.secondary ? codeToName(language.secondary) : null;
  const nameLabel = secondaryName ? `${primaryName} / ${secondaryName}` : primaryName;
  const label = showConfidence
    ? `${nameLabel} · ${language.confidence.toFixed(2)}`
    : nameLabel;
  const tooltip =
    `Detected language: ${primaryName} (${language.code})` +
    (language.secondary ? ` + ${secondaryName} (${language.secondary})` : '') +
    ` — confidence ${language.confidence.toFixed(2)}, source ${language.source.toLowerCase()}.` +
    ` Feeds per-profile language policy (ALLOW_ALL / ALLOW_LISTED / BLOCK_LISTED).`;

  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        label={label}
        sx={{
          fontSize: 10,
          fontWeight: 600,
          height: 20,
          bgcolor: pcolors.blue[50],
          color: pcolors.blue[700],
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}
      />
    </Tooltip>
  );
}
